import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useTheme } from "../hooks/useTheme";
import { unreadCount, eventsURL } from "../api/support";
import Icon, { type IconName } from "./icons";
import { Logo, LogoMark } from "./Logo";

const ITEMS: { to: string; icon: IconName; label: string }[] = [
  { to: "/customers", icon: "users", label: "Müşteriler" },
  { to: "/muhasebe", icon: "creditCard", label: "Muhasebe" },
  { to: "/destek", icon: "headset", label: "Destek" },
  { to: "/ai", icon: "cpu", label: "AI & Fiyat" },
  { to: "/corpus", icon: "book", label: "Mevzuat" },
  { to: "/mevzuat-sorulari", icon: "inbox", label: "Mevzuat Soruları" },
  { to: "/mail-takip", icon: "mail", label: "Mail Takibi" },
  { to: "/ziyaretciler", icon: "globe", label: "Sitede Gezinenler" },
  { to: "/listeden-cikanlar", icon: "close", label: "Listeden Çıkanlar" },
  { to: "/ayarlar", icon: "settings", label: "Ayarlar" },
];

const COLLAPSE_KEY = "admin_nav_collapsed";

export default function AdminNav() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [supportCount, setSupportCount] = useState(0);

  // Destek yanıtı bekleyen talep sayısı — canlı (SSE ile her olayda tazelenir).
  useEffect(() => {
    const refresh = () => { void unreadCount().then(setSupportCount).catch(() => {}); };
    refresh();
    const es = new EventSource(eventsURL());
    const onEvt = () => refresh();
    es.addEventListener("ticket_created", onEvt);
    es.addEventListener("message_added", onEvt);
    es.addEventListener("ticket_updated", onEvt);
    return () => es.close();
  }, []);

  function toggleCollapse() {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      return next;
    });
  }

  return (
    <nav className={"adminnav" + (collapsed ? " collapsed" : "")}>
      <div className="adminnav-top">
        {/* Daraltılmışken yalnız monogram: 220px çubuk 64px'e iniyor, tam logo
            sığmaz. Monogram tam logonun sol parçasıyla aynı işaret, o yüzden
            kullanıcı için kopukluk yok. "ADMIN" etiketi adminnav-label içinde,
            çünkü daraltmada gizlenen sınıf o. */}
        <div className="adminnav-brand">
          {collapsed ? <LogoMark className="brand-logo-mark" /> : <Logo className="brand-logo" />}
          <span className="adminnav-label"><small>Admin</small></span>
        </div>
        <button
          className="adminnav-collapse"
          onClick={toggleCollapse}
          title={collapsed ? "Genişlet" : "Daralt"}
          aria-label={collapsed ? "Genişlet" : "Daralt"}
        >
          <Icon name={collapsed ? "chevronRight" : "chevronLeft"} size={16} />
        </button>
      </div>

      <div className="adminnav-items">
        {ITEMS.map((it) => {
          const badge = it.to === "/destek" && supportCount > 0 ? supportCount : 0;
          return (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) => "adminnav-item" + (isActive ? " active" : "")}
            title={badge ? `${it.label} (${badge} cevap bekliyor)` : it.label}
          >
            <span className="adminnav-ico" aria-hidden>
              <Icon name={it.icon} />
              {badge > 0 && <span className="adminnav-dot" aria-hidden />}
            </span>
            <span className="adminnav-label">{it.label}</span>
            {badge > 0 && <span className="adminnav-badge">{badge}</span>}
          </NavLink>
          );
        })}
      </div>

      <div className="adminnav-foot">
        <button
          className="adminnav-tool"
          onClick={toggle}
          title={theme === "dark" ? "Açık tema" : "Koyu tema"}
        >
          <span className="adminnav-ico" aria-hidden><Icon name={theme === "dark" ? "sun" : "moon"} /></span>
          <span className="adminnav-label">{theme === "dark" ? "Açık tema" : "Koyu tema"}</span>
        </button>
        {user && <div className="adminnav-who adminnav-label" title={user.email}>{user.email}</div>}
        <button className="adminnav-logout" onClick={logout} title="Çıkış">
          <span className="adminnav-ico" aria-hidden><Icon name="logOut" /></span>
          <span className="adminnav-label">Çıkış</span>
        </button>
      </div>
    </nav>
  );
}
